import { useState, useEffect } from 'react'
import { isHttpUrl } from '@super/shared-types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type ScrapeMode = 'delete' | 'append' | 'update'

interface ListingScrapeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (url: string, mode: ScrapeMode) => void
  initialUrl?: string
  initialMode?: ScrapeMode
  dialogMode?: 'manual' | 'scheduled' // 'manual' shows URL + mode, 'scheduled' shows only URL (defaults to 'delete')
}

export function ListingScrapeDialog({
  open,
  onOpenChange,
  onSubmit,
  initialUrl,
  initialMode = 'delete',
  dialogMode = 'scheduled',
}: ListingScrapeDialogProps) {
  const [url, setUrl] = useState('')
  const [mode, setMode] = useState<ScrapeMode>(initialMode)

  useEffect(() => {
    if (open) {
      setUrl(initialUrl || '')
      // For scheduled sync, always use 'delete' mode; for manual, use initialMode
      setMode(dialogMode === 'scheduled' ? 'delete' : initialMode || 'delete')
    }
  }, [open, initialUrl, initialMode, dialogMode])

  const handleSubmit = () => {
    if (!url.trim()) return

    onSubmit?.(url, mode)
    handleCancel()
  }

  const handleCancel = () => {
    setUrl('')
    setMode('delete')
    onOpenChange(false)
  }

  const canSubmit = url.trim() && isHttpUrl(url)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {dialogMode === 'manual'
              ? 'Sync Listings'
              : 'Schedule Listing Sync'}
          </DialogTitle>
          <DialogDescription>
            {dialogMode === 'manual'
              ? 'Enter the URL of the listings page to sync immediately. This will fetch and update your listings right away.'
              : 'Enter the URL of the listings page to sync automatically every 6 hours. Listings will be replaced with new ones from the URL.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="listing-url">Listing URL</Label>
            <Input
              id="listing-url"
              type="url"
              placeholder="https://example.com/listings"
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The URL of the page containing the listings to sync
            </p>
          </div>

          {dialogMode === 'manual' && (
            <div className="space-y-2">
              <Label htmlFor="scrape-mode">Sync Behavior</Label>
              <Select
                value={mode}
                onValueChange={value => setMode(value as ScrapeMode)}
              >
                <SelectTrigger id="scrape-mode" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delete">Delete current</SelectItem>
                  <SelectItem value="append">Append only</SelectItem>
                  <SelectItem value="update">Update and append</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {mode === 'update' &&
                  'Update existing listings and add new ones (recommended)'}
                {mode === 'delete' &&
                  'Remove all existing listings and replace with new ones'}
                {mode === 'append' &&
                  'Keep existing listings and add new ones only'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="form"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {dialogMode === 'manual'
              ? 'Sync Now'
              : initialUrl
                ? 'Update schedule'
                : 'Schedule sync'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
